import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Card, Empty } from "../panel/PanelShell";
import { Field, SelectField, inputCls } from "../forms/Fields";
import { api, formatApiError } from "../../lib/api";
import { inr, fmtDate } from "../../lib/format";

const CATS = ["Retirement", "Child Education", "Home", "Emergency Fund", "Wealth Creation", "Travel", "Other"];
const BLANK = { title: "", category: "Wealth Creation", target_amount: "", target_date: "", current_savings: 0, notes: "" };

function GoalDialog({ open, onClose, initial, onSaved }) {
  const [form, setForm] = useState(BLANK);
  useEffect(() => { setForm(initial || BLANK); }, [initial, open]);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const save = async (e) => {
    e.preventDefault();
    const body = { ...form, target_amount: Number(form.target_amount), current_savings: Number(form.current_savings || 0) };
    try {
      if (initial?.id) await api.put(`/portal/goals/${initial.id}`, body);
      else await api.post("/portal/goals", body);
      toast.success("Goal saved");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit goal" : "New financial goal"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4" data-testid="goal-form">
          <Field label="Goal title" id="g-title"><input id="g-title" required minLength={2} className={inputCls} value={form.title} onChange={set("title")} data-testid="goal-title-input" /></Field>
          <SelectField label="Category" id="g-cat" value={form.category} onChange={set("category")} options={CATS} testid="goal-category-select" />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Target amount (₹)" id="g-amt"><input id="g-amt" type="number" min={1} required className={inputCls} value={form.target_amount} onChange={set("target_amount")} data-testid="goal-amount-input" /></Field>
            <Field label="Target date" id="g-date"><input id="g-date" type="date" required className={inputCls} value={form.target_date} onChange={set("target_date")} data-testid="goal-date-input" /></Field>
          </div>
          <Field label="Saved so far (₹)" id="g-saved"><input id="g-saved" type="number" min={0} className={inputCls} value={form.current_savings} onChange={set("current_savings")} data-testid="goal-saved-input" /></Field>
          <Field label="Notes" id="g-notes"><input id="g-notes" className={inputCls} value={form.notes} onChange={set("notes")} data-testid="goal-notes-input" /></Field>
          <button data-testid="goal-save-btn" className="rounded-full bg-navy py-3 text-sm font-semibold text-white hover:bg-navy-700">Save goal</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function GoalsTab({ onChange }) {
  const [goals, setGoals] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/portal/goals").then((r) => { setGoals(r.data); onChange?.(); });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id) => { await api.delete(`/portal/goals/${id}`); toast.success("Goal removed"); load(); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">Track progress towards the goals that matter to you.</p>
        <button onClick={() => { setEditing(null); setOpen(true); }} data-testid="goal-add-btn" className="inline-flex items-center gap-2 rounded-full bg-mint px-5 py-2.5 text-sm font-semibold text-navy"><Plus size={16} /> Add goal</button>
      </div>
      {!goals.length && <Empty text="No goals yet. Add your first financial goal." testid="goals-empty" />}
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {goals.map((g) => {
          const pct = Math.min(100, (g.current_savings / g.target_amount) * 100);
          return (
            <Card key={g.id} data-testid={`goal-card-${g.id}`}>
              <div className="flex items-start justify-between">
                <div><span className="text-xs font-semibold uppercase tracking-wider text-gold-600">{g.category}</span><h3 className="mt-1 text-lg font-bold text-navy">{g.title}</h3></div>
                <div className="flex gap-1">
                  <button onClick={() => { setEditing(g); setOpen(true); }} data-testid={`goal-edit-${g.id}`} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-navy" aria-label="Edit"><Pencil size={15} /></button>
                  <button onClick={() => remove(g.id)} data-testid={`goal-delete-${g.id}`} className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600" aria-label="Delete"><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="mt-6 h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-mint" style={{ width: `${pct}%` }} /></div>
              <div className="mt-3 flex justify-between text-sm"><span className="font-num text-navy">{inr(g.current_savings)}</span><span className="font-num text-slate-500">of {inr(g.target_amount)}</span></div>
              <p className="mt-3 text-xs text-slate-500">Target: {fmtDate(g.target_date)} · {pct.toFixed(0)}% funded</p>
            </Card>
          );
        })}
      </div>
      <GoalDialog open={open} onClose={() => setOpen(false)} initial={editing} onSaved={load} />
    </div>
  );
}
